import {
  RARITY_PRESENTATION,
  levelForTotalXp,
  totalXpAtLevel,
  xpToNextLevel,
} from "@ninja/domain";
import { escapeHtml } from "./escapeHtml.js";
import { renderNinjaOutfitImage } from "./NinjaOutfitPortrait.js";

export interface NinjaRosterMember {
  id: string;
  name: string;
  appearance_id: string;
  rarity: keyof typeof RARITY_PRESENTATION;
  rank: string;
  level: number;
  total_experience: string | number;
  current_health: number;
  maximum_health: number;
  current_chakra: number;
  maximum_chakra: number;
  life_status?: "available" | "incapacitated" | "hospitalized" | "dismissed";
  activity_lock?: string | null;
  hospital_case_status?: "waiting" | "treating" | null;
}

export interface NinjaRosterPanelOptions {
  root: HTMLElement;
  ninjas: readonly NinjaRosterMember[];
  savedActiveNinjaIds: readonly string[];
  rosterCapacity: number;
  squadCapacity: number;
  barracksLevel: number;
  saveActiveSquad: (ninjaIds: readonly string[]) => Promise<void>;
  openInventory: (ninjaId: string) => void;
  openBarracks: () => void;
}

type AvailabilityFilter = "all" | "available" | "unavailable";
type RosterSort = "name" | "level" | "rarity" | "rank";

const RARITY_ORDER = Object.keys(RARITY_PRESENTATION) as Array<
  keyof typeof RARITY_PRESENTATION
>;

export class NinjaRosterPanel {
  private readonly savedIds: string[];
  private selectedIds: string[];
  private search = "";
  private rarityFilter = "";
  private rankFilter = "";
  private availabilityFilter: AvailabilityFilter = "all";
  private rosterSort: RosterSort = "name";
  private onlySquadMembers = false;
  private saving = false;

  constructor(private readonly options: NinjaRosterPanelOptions) {
    const knownIds = new Set(options.ninjas.map((ninja) => ninja.id));
    this.savedIds = options.savedActiveNinjaIds.filter((id) =>
      knownIds.has(id),
    );
    this.selectedIds = [...this.savedIds];
  }

  mount(): void {
    this.options.root.innerHTML = this.renderLayout();
    this.bindEvents();
    this.renderDynamicContent();
  }

  private renderLayout(): string {
    const rarityOptions = RARITY_ORDER.map(
      (rarity) =>
        `<option value="${rarity}">${escapeHtml(RARITY_PRESENTATION[rarity].label)}</option>`,
    ).join("");
    const rankOptions = [
      ...new Set(this.options.ninjas.map((ninja) => ninja.rank)),
    ]
      .sort((left, right) => left.localeCompare(right))
      .map(
        (rank) =>
          `<option value="${escapeHtml(rank)}">${escapeHtml(rank)}</option>`,
      )
      .join("");

    return `
      <section class="ninja-roster-page" aria-labelledby="roster-title">
        <header class="ninja-roster-heading">
          <div>
            <p class="eyebrow">THE NINJA ARCHIVE</p>
            <h1 id="roster-title">Your roster</h1>
            <p class="muted">Search your ninjas, compare their status, and build the active squad.</p>
          </div>
          <div class="roster-capacity-summary" aria-label="Roster capacity">
            <strong>${this.options.ninjas.length}<span> / ${this.options.rosterCapacity}</span></strong>
            <small>ROSTER · BARRACKS LV ${this.options.barracksLevel}</small>
          </div>
        </header>

        <section class="roster-team-panel" aria-labelledby="roster-team-title">
          <div class="roster-team-heading">
            <div>
              <p class="eyebrow" id="roster-team-title">ACTIVE SQUAD</p>
              <p class="roster-team-count" id="roster-team-count"></p>
            </div>
            <span class="roster-draft-state" id="roster-draft-state" aria-live="polite"></span>
          </div>
          <div class="roster-team-slots" id="roster-team-slots" aria-label="Selected ninjas"></div>
        </section>

        <section class="roster-filters" aria-label="Filter ninjas">
          <label class="roster-search-field">SEARCH BY NAME
            <input type="search" id="roster-search" placeholder="Type a ninja name" autocomplete="off" />
          </label>
          <label>RARITY
            <select id="roster-rarity"><option value="">All rarities</option>${rarityOptions}</select>
          </label>
          <label>RANK
            <select id="roster-rank"><option value="">All ranks</option>${rankOptions}</select>
          </label>
          <label>STATUS
            <select id="roster-availability"><option value="all">All statuses</option><option value="available">Available</option><option value="unavailable">Unavailable</option></select>
          </label>
          <label>SORT BY
            <select id="roster-sort"><option value="name">Name</option><option value="level">Level</option><option value="rarity">Rarity</option><option value="rank">Rank</option></select>
          </label>
          <label class="roster-squad-filter"><input type="checkbox" id="roster-only-squad" /> <span>Only active squad</span></label>
          <button type="button" class="button button-quiet roster-clear-filters" data-roster-clear>Clear filters</button>
        </section>

        <div class="roster-results-bar">
          <p id="roster-results-count" aria-live="polite"></p>
          <div class="roster-page-actions">
            <button type="button" class="button button-outline" data-roster-barracks>Barracks</button>
            <button type="button" class="button button-quiet" data-roster-reset disabled>Discard changes</button>
            <button type="button" class="button button-primary" data-roster-save disabled>Save active squad</button>
          </div>
        </div>

        <div class="roster-card-grid" id="roster-card-grid"></div>
        <p class="roster-notice" id="roster-notice" role="status" aria-live="polite"></p>
      </section>`;
  }

  private bindEvents(): void {
    const root = this.options.root;
    root.addEventListener("input", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.id === "roster-search") {
        this.search = target.value.trim().toLocaleLowerCase();
        this.renderCards();
      }
    });
    root.addEventListener("change", (event) => {
      const target = event.target;
      if (!(
        target instanceof HTMLSelectElement ||
        target instanceof HTMLInputElement
      ))
        return;

      switch (target.id) {
        case "roster-rarity":
          this.rarityFilter = target.value;
          break;
        case "roster-rank":
          this.rankFilter = target.value;
          break;
        case "roster-availability":
          this.availabilityFilter = target.value as AvailabilityFilter;
          break;
        case "roster-sort":
          this.rosterSort = target.value as RosterSort;
          break;
        case "roster-only-squad":
          this.onlySquadMembers = (target as HTMLInputElement).checked;
          break;
        default:
          return;
      }
      this.renderCards();
    });
    root.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLButtonElement>("button");
      if (!button || !root.contains(button)) return;

      if (button.matches("[data-roster-clear]")) {
        this.clearFilters();
      } else if (button.matches("[data-roster-barracks]")) {
        this.options.openBarracks();
      } else if (button.matches("[data-roster-save]")) {
        void this.saveSquad();
      } else if (button.matches("[data-roster-reset]")) {
        this.selectedIds = [...this.savedIds];
        this.setNotice("");
        this.renderDynamicContent();
      } else if (button.dataset.toggleSquad) {
        this.toggleSquadMember(button.dataset.toggleSquad);
      } else if (button.dataset.removeSquadMember) {
        this.removeSquadMember(button.dataset.removeSquadMember);
      } else if (button.dataset.rosterInventory) {
        this.options.openInventory(button.dataset.rosterInventory);
      }
    });
  }

  private clearFilters(): void {
    this.search = "";
    this.rarityFilter = "";
    this.rankFilter = "";
    this.availabilityFilter = "all";
    this.rosterSort = "name";
    this.onlySquadMembers = false;

    const root = this.options.root;
    root.querySelector<HTMLInputElement>("#roster-search")!.value = "";
    root.querySelector<HTMLSelectElement>("#roster-rarity")!.value = "";
    root.querySelector<HTMLSelectElement>("#roster-rank")!.value = "";
    root.querySelector<HTMLSelectElement>("#roster-availability")!.value =
      "all";
    root.querySelector<HTMLSelectElement>("#roster-sort")!.value = "name";
    root.querySelector<HTMLInputElement>("#roster-only-squad")!.checked = false;
    this.renderCards();
  }

  private toggleSquadMember(ninjaId: string): void {
    const ninja = this.options.ninjas.find((entry) => entry.id === ninjaId);
    if (!ninja) return;

    if (this.selectedIds.includes(ninjaId)) {
      this.removeSquadMember(ninjaId);
      return;
    }
    const status = this.availability(ninja);
    if (!status.available) {
      this.setNotice(
        `${ninja.name} cannot join the squad while ${status.message.toLocaleLowerCase()}.`,
      );
      return;
    }
    if (this.selectedIds.length >= this.options.squadCapacity) {
      this.setNotice(
        `The Barracks currently allows ${this.options.squadCapacity} ninjas in the active squad.`,
      );
      return;
    }

    this.selectedIds = [...this.selectedIds, ninjaId];
    this.setNotice("");
    this.renderDynamicContent();
  }

  private removeSquadMember(ninjaId: string): void {
    if (!this.selectedIds.includes(ninjaId)) return;
    this.selectedIds = this.selectedIds.filter((id) => id !== ninjaId);
    this.setNotice("");
    this.renderDynamicContent();
  }

  private async saveSquad(): Promise<void> {
    if (this.saving || !this.hasUnsavedChanges()) return;
    const idsToSave = [...this.selectedIds];
    this.saving = true;
    this.setNotice("Saving active squad…");
    this.renderDynamicContent();
    try {
      await this.options.saveActiveSquad(idsToSave);
      this.savedIds.splice(0, this.savedIds.length, ...idsToSave);
      this.setNotice("Active squad saved.", "success");
    } catch (error) {
      this.setNotice((error as Error).message, "error");
    } finally {
      this.saving = false;
      this.renderDynamicContent();
    }
  }

  private renderDynamicContent(): void {
    this.renderSquadSlots();
    this.renderCards();
    this.updateActionState();
  }

  private renderSquadSlots(): void {
    const root = this.options.root;
    const count = root.querySelector<HTMLElement>("#roster-team-count");
    const slots = root.querySelector<HTMLElement>("#roster-team-slots");
    if (!count || !slots) return;

    count.textContent = `${this.selectedIds.length} / ${this.options.squadCapacity} selected`;
    const members = this.selectedIds
      .map((id) => this.options.ninjas.find((ninja) => ninja.id === id))
      .filter((ninja): ninja is NinjaRosterMember => Boolean(ninja));
    const emptySlots = Math.max(0, this.options.squadCapacity - members.length);
    slots.innerHTML = `${members
      .map(
        (ninja) =>
          `<button type="button" class="roster-team-slot filled" data-remove-squad-member="${escapeHtml(ninja.id)}" title="Remove ${escapeHtml(ninja.name)} from the active squad" aria-label="Remove ${escapeHtml(ninja.name)} from the active squad" ${this.saving ? "disabled" : ""}>${renderNinjaOutfitImage(ninja.appearance_id, ninja.name, { className: "roster-slot-outfit" })}<span>${escapeHtml(ninja.name)}</span><b aria-hidden="true">×</b></button>`,
      )
      .join("")}${Array.from(
      { length: emptySlots },
      (_, index) =>
        `<div class="roster-team-slot empty" aria-label="Empty squad slot ${members.length + index + 1}"><span aria-hidden="true">+</span><small>OPEN SLOT</small></div>`,
    ).join("")}`;
  }

  private renderCards(): void {
    const root = this.options.root;
    const grid = root.querySelector<HTMLElement>("#roster-card-grid");
    const results = root.querySelector<HTMLElement>("#roster-results-count");
    if (!grid || !results) return;

    const visibleNinjas = this.filteredNinjas();
    results.textContent = `Showing ${visibleNinjas.length} of ${this.options.ninjas.length} ninjas`;
    if (this.options.ninjas.length === 0) {
      grid.innerHTML =
        '<div class="roster-empty-state"><strong>Your roster is empty.</strong><span>Visit the Academy to recruit your first ninja.</span></div>';
      return;
    }
    if (visibleNinjas.length === 0) {
      grid.innerHTML =
        '<div class="roster-empty-state"><strong>No ninjas match these filters.</strong><span>Clear or change a filter to see more of your roster.</span><button type="button" class="button button-outline" data-roster-clear>Clear filters</button></div>';
      return;
    }

    grid.innerHTML = visibleNinjas
      .map((ninja) => this.renderCard(ninja))
      .join("");
  }

  private filteredNinjas(): NinjaRosterMember[] {
    const rarityOrder = new Map(
      RARITY_ORDER.map((rarity, index) => [rarity, index]),
    );
    return this.options.ninjas
      .filter((ninja) => {
        if (
          this.search &&
          !ninja.name.toLocaleLowerCase().includes(this.search)
        )
          return false;
        if (this.rarityFilter && ninja.rarity !== this.rarityFilter)
          return false;
        if (this.rankFilter && ninja.rank !== this.rankFilter) return false;
        const available = this.availability(ninja).available;
        if (this.availabilityFilter === "available" && !available) return false;
        if (this.availabilityFilter === "unavailable" && available)
          return false;
        if (this.onlySquadMembers && !this.selectedIds.includes(ninja.id))
          return false;
        return true;
      })
      .sort((left, right) => {
        switch (this.rosterSort) {
          case "level":
            return (
              right.level - left.level || left.name.localeCompare(right.name)
            );
          case "rarity":
            return (
              (rarityOrder.get(right.rarity) ?? -1) -
                (rarityOrder.get(left.rarity) ?? -1) ||
              left.name.localeCompare(right.name)
            );
          case "rank":
            return (
              left.rank.localeCompare(right.rank) ||
              left.name.localeCompare(right.name)
            );
          default:
            return left.name.localeCompare(right.name);
        }
      });
  }

  private renderCard(ninja: NinjaRosterMember): string {
    const rarity =
      RARITY_PRESENTATION[ninja.rarity] ?? RARITY_PRESENTATION.common;
    const totalExperience = Number(ninja.total_experience);
    const level = levelForTotalXp(totalExperience);
    const healthPercent = this.percent(
      ninja.current_health,
      ninja.maximum_health,
    );
    const chakraPercent = this.percent(
      ninja.current_chakra,
      ninja.maximum_chakra,
    );
    const selected = this.selectedIds.includes(ninja.id);
    const status = this.availability(ninja);
    const addDisabled =
      this.saving ||
      (!selected &&
        (!status.available ||
          this.selectedIds.length >= this.options.squadCapacity));
    const toggleTitle = selected
      ? `Remove ${ninja.name} from the active squad`
      : !status.available
        ? `${ninja.name} is ${status.message.toLocaleLowerCase()}`
        : this.selectedIds.length >= this.options.squadCapacity
          ? `Squad capacity is ${this.options.squadCapacity}`
          : `Add ${ninja.name} to the active squad`;
    const xp = Math.max(0, totalExperience - totalXpAtLevel(level));
    const requiredXp = xpToNextLevel(level);
    const levelProgress = this.percent(xp, requiredXp);

    return `<article class="roster-card${selected ? " is-selected" : ""}${status.available ? "" : " is-unavailable"}" style="--roster-rarity:${rarity.color}">
      <div class="roster-card-main">
        <div class="roster-card-portrait" aria-label="${escapeHtml(ninja.name)} portrait">${renderNinjaOutfitImage(ninja.appearance_id, ninja.name, { className: "roster-card-outfit" })}</div>
        <div class="roster-card-identity">
          <div class="roster-card-labels"><span class="roster-rarity-label">${escapeHtml(rarity.label)}</span><span class="roster-rank-label">${escapeHtml(ninja.rank)}</span></div>
          <h2 title="${escapeHtml(ninja.name)}">${escapeHtml(ninja.name)}</h2>
          <div class="roster-level-row"><span>LEVEL <b>${level}</b></span><small>${Math.floor(xp).toLocaleString()} / ${requiredXp.toLocaleString()} XP</small></div>
          <div class="roster-progress-track" aria-label="Experience progress"><i style="width:${levelProgress}%"></i></div>
          <span class="roster-status-chip ${status.available ? "available" : "unavailable"}" title="${escapeHtml(status.message)}">${escapeHtml(status.label)}</span>
        </div>
      </div>
      <div class="roster-card-resources">
        ${this.resourceRow("HP", ninja.current_health, ninja.maximum_health, healthPercent, "health")}
        ${this.resourceRow("CHAKRA", ninja.current_chakra, ninja.maximum_chakra, chakraPercent, "chakra")}
      </div>
      <footer class="roster-card-actions">
        <button type="button" class="button button-outline roster-details-button" data-roster-inventory="${escapeHtml(ninja.id)}">Inventory &amp; status</button>
        <button type="button" class="button ${selected ? "button-selected" : "button-primary"} roster-squad-button" data-toggle-squad="${escapeHtml(ninja.id)}" aria-pressed="${selected}" ${addDisabled ? `disabled title="${escapeHtml(toggleTitle)}"` : `title="${escapeHtml(toggleTitle)}"`}>${selected ? "In squad · Remove" : status.available ? "Add to squad" : escapeHtml(status.label)}</button>
      </footer>
    </article>`;
  }

  private resourceRow(
    label: string,
    current: number,
    maximum: number,
    percent: number,
    kind: "health" | "chakra",
  ): string {
    return `<div class="roster-resource-row"><span>${label}</span><div class="roster-resource-track"><i class="${kind}" style="width:${percent}%"></i></div><b>${Math.max(0, Math.floor(current)).toLocaleString()} / ${Math.max(0, Math.floor(maximum)).toLocaleString()}</b></div>`;
  }

  private availability(ninja: NinjaRosterMember): {
    available: boolean;
    label: string;
    message: string;
  } {
    if (ninja.hospital_case_status === "waiting")
      return {
        available: false,
        label: "WAITING FOR A BED",
        message: "Waiting for a hospital bed",
      };
    if (
      ninja.hospital_case_status === "treating" ||
      ninja.life_status === "hospitalized"
    )
      return {
        available: false,
        label: "IN TREATMENT",
        message: "In hospital treatment",
      };
    if (ninja.life_status && ninja.life_status !== "available")
      return {
        available: false,
        label: "INCAPACITATED",
        message: "Incapacitated",
      };
    if (ninja.activity_lock)
      return {
        available: false,
        label: "ON ACTIVITY",
        message: "Assigned to an activity",
      };
    return {
      available: true,
      label: "AVAILABLE",
      message: "Available in the village",
    };
  }

  private hasUnsavedChanges(): boolean {
    return (
      this.selectedIds.length !== this.savedIds.length ||
      this.selectedIds.some((id, index) => id !== this.savedIds[index])
    );
  }

  private updateActionState(): void {
    const count = this.options.root.querySelector<HTMLElement>(
      "#roster-draft-state",
    );
    const save =
      this.options.root.querySelector<HTMLButtonElement>("[data-roster-save]");
    const reset = this.options.root.querySelector<HTMLButtonElement>(
      "[data-roster-reset]",
    );
    const dirty = this.hasUnsavedChanges();
    if (count) count.textContent = dirty ? "UNSAVED CHANGES" : "SQUAD SAVED";
    if (save) {
      save.disabled = this.saving || !dirty;
      save.textContent = this.saving ? "Saving…" : "Save active squad";
    }
    if (reset) reset.disabled = this.saving || !dirty;
  }

  private setNotice(
    message: string,
    kind: "success" | "error" | "neutral" = "neutral",
  ): void {
    const notice =
      this.options.root.querySelector<HTMLElement>("#roster-notice");
    if (!notice) return;
    notice.textContent = message;
    notice.dataset.kind = message ? kind : "";
  }

  private percent(value: number, maximum: number): number {
    if (!Number.isFinite(value) || !Number.isFinite(maximum) || maximum <= 0)
      return 0;
    return Math.max(0, Math.min(100, (value / maximum) * 100));
  }
}
